import { useMemo } from "react";

import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import StatCard from "@/components/UI/Cards/StatCard";
import PageHeader from "@/components/UI/PageHeader";
import { useFetch } from "@/hooks/FetchHook";

const DATASET = "immunogenicity";

const CONSTANTS = {
  wikiLink: "/wiki/guides/exploration-analysis-immunogenicity",
  idLink: undefined,
};

type ImmunogenicityRow = {
  ada_ever_positive: boolean | null;
  ada_treatment_emergent: boolean | null;
  neutralizing_antibodies: boolean | null;
  ada_impact_on_pk: boolean | null;
  ada_peak_titer: string | null;
  first_positive_cycle: string | null;
};

function pct(n: number, d: number): string {
  return d ? `${((n / d) * 100).toFixed(1)}%` : "—";
}

/** A small, self-contained horizontal-bar distribution (no chart lib needed). */
function Distribution({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; count: number }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-lg bg-white p-4 shadow-omni">
      <p className="mb-3 text-sm font-medium text-[#556C75]">{title}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-[#7F7F7F]">
          No positive subjects to summarize.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-xs text-[#2B4752]">
                {r.label}
              </span>
              <div className="h-4 flex-1 rounded bg-[#EEF4F7]">
                <div
                  className="h-4 rounded bg-[#64BDE1]"
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
              <span className="w-8 shrink-0 text-right text-xs text-[#2B4752]">
                {r.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Immunogenicity() {
  const { data, isLoading } = useFetch({
    endpointSuffix: `filters/data/?dataset_type=${DATASET}&pagination=${encodeURIComponent(
      JSON.stringify({ page_size: 2000 }),
    )}`,
    method: "GET",
    extraQueryKey: "-immunogenicity-summary",
  });

  const summary = useMemo(() => {
    const rows = ((data as { results?: ImmunogenicityRow[] } | undefined)
      ?.results ?? []) as ImmunogenicityRow[];
    const total = rows.length;
    const countTrue = (key: keyof ImmunogenicityRow) =>
      rows.filter((r) => r[key] === true).length;

    // NAb is only assessed for ADA-positive subjects, so rate it over the
    // subjects that actually have a NAb result (true or false), not all subjects.
    const nabPos = countTrue("neutralizing_antibodies");
    const nabTested = rows.filter(
      (r) =>
        r.neutralizing_antibodies === true ||
        r.neutralizing_antibodies === false,
    ).length;

    const titerCounts: Record<string, number> = {};
    for (const r of rows) {
      const t = (r.ada_peak_titer ?? "").trim();
      if (t) titerCounts[t] = (titerCounts[t] ?? 0) + 1;
    }
    const titerDenom = (label: string) => {
      const m = label.match(/1:(\d+)/);
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    const titerRows = Object.entries(titerCounts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => titerDenom(a.label) - titerDenom(b.label));

    const impactRows = [
      { label: "PK impact", count: countTrue("ada_impact_on_pk") },
      {
        label: "No PK impact",
        count: rows.filter((r) => r.ada_impact_on_pk === false).length,
      },
    ];

    return {
      total,
      everPos: countTrue("ada_ever_positive"),
      te: countTrue("ada_treatment_emergent"),
      nabPos,
      nabTested,
      titerRows,
      impactRows,
    };
  }, [data]);

  const cardValue = (n: number, d: number) =>
    isLoading ? "…" : `${n} (${pct(n, d)})`;

  return (
    <div className="w-full p-4">
      <div className="mb-4">
        <PageHeader
          title="Analysis: Immunogenicity"
          tooltipText="Anti-drug antibody (ADA) incidence and characterization for the trial: ever-positive and treatment-emergent rates, neutralizing antibodies, peak-titer distribution, and observed impact on pharmacokinetics."
        />
      </div>

      {/* ADA incidence cards */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label="ADA Ever-Positive"
          value={cardValue(summary.everPos, summary.total)}
          description={`of ${summary.total} subjects with an ADA result`}
        />
        <StatCard
          label="Treatment-Emergent ADA"
          value={cardValue(summary.te, summary.total)}
          description="Newly induced or boosted relative to baseline"
        />
        <StatCard
          label="Neutralizing Antibodies"
          value={cardValue(summary.nabPos, summary.nabTested)}
          description={`of ${summary.nabTested} NAb-tested subjects`}
        />
      </div>

      {/* Distributions */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Distribution
          title="Peak ADA titer distribution"
          rows={summary.titerRows}
        />
        <Distribution
          title="ADA impact on pharmacokinetics"
          rows={summary.impactRows}
        />
      </div>

      {/* Subject-level table */}
      <MetadataDashboardTemplate
        type={DATASET}
        constants={CONSTANTS}
        selectionIdentifierColumn="subject"
      />
    </div>
  );
}
